import type { Command } from '../service';
import type {
  Context,
  Entity,
  Evidence,
  Kind,
  Role,
  SessionIdentity,
  Snapshot,
} from '../types';
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };
export type ErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'INVALID_TRANSITION'
  | 'UNAVAILABLE';
export class DomainFault extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'DomainFault';
  }
}
export interface QueryInput {
  keyword?: string;
  kinds?: Kind[];
  memberUuid?: string;
  from?: string;
  to?: string;
  status?: string;
  page?: number;
  pageSize?: number;
  sort?: 'createdAt' | 'dueAt' | 'title';
  descending?: boolean;
}
export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  revision: number;
}
export interface EvidenceRef extends Evidence {
  fileUuid?: string;
  scope?: string;
  sourceRevision?: number;
}
export interface SearchDocument {
  id: string;
  entityUuid: string;
  title: string;
  text: string;
  kind: Kind;
  memberUuid: string;
  occurredAt: string;
  evidence?: EvidenceRef;
}
export interface SearchHit extends Omit<SearchDocument, 'text'> {
  excerpt: string;
  matchedTerms: string[];
  reason: string;
}
export interface BusinessInsight {
  uuid: string;
  title: string;
  reason: string;
  sourceUuid: string;
  sourceRevision: number;
  scope: string;
  missingInformation: string[];
}
export interface ActionRecommendation {
  id: string;
  label: string;
  sourceUuid: string;
  action: string;
  fields?: Record<string, string>;
}
export interface ActionPreview {
  uuid: string;
  createdAt: string;
  expiresAt: string;
  contextKey: string;
  snapshotRevision: number;
  sourceVersions: Record<string, number>;
  recommendation: ActionRecommendation;
  impact: string;
}
export interface OutcomeSnapshot {
  uuid: string;
  scope: string;
  asOf: string;
  metric: string;
  numerator: number;
  denominator: number | null;
  unit: string;
  sourceUuids: string[];
  definitionVersion: string;
}
export interface PublishedConfigRef {
  uuid: string;
  revision: number;
  publishedAt: string;
}
export interface UsageEntry {
  uuid: string;
  businessKey: string;
  sourceUuid: string;
  occurredAt: string;
  quantity: number;
  unit: string;
  status: 'succeeded' | 'failed';
  pricing: PublishedConfigRef;
  amountInCents: number;
}
export interface ConfirmedFileInput {
  uuid: string;
  contentHash: string;
  deviceUuid?: string;
  bindingUuid?: string;
  occurredAt: string;
  campus: string;
  memberUuid: string;
  durationMs: number;
  mimeType: string;
  confirmedAt: string;
  sourceRevision: number;
}
export interface PageRegistration {
  id: string;
  path: string;
  resource: string;
  parentPath: string;
  returnTo: string;
}
export interface DomainReadContext {
  readonly identity: Readonly<SessionIdentity>;
  readonly context: Readonly<Context>;
  readonly now: string;
  readonly revision: number;
  members(): Pick<
    Entity,
    'uuid' | 'memberUuid' | 'title' | 'campus' | 'status'
  >[];
  list(kind: Kind): Entity[];
  get(uuid: string | undefined): Entity | undefined;
  data(): Readonly<JsonObject>;
}
export interface DomainCommandContext extends DomainReadContext {
  readonly commandUuid: string;
  append(kind: Kind, title: string, extra?: Partial<Entity>): Entity;
  touch(row: Entity): void;
  notify(row: Entity, title: string): void;
  task(row: Entity, title: string): void;
  setData(value: JsonObject): void;
}
export interface DomainCommand {
  label?: string;
  roles: Role[];
  kind?: Kind;
  execute(ctx: DomainCommandContext, input: Command): Entity | undefined;
}
export interface DomainMigration {
  version: number;
  up(state: Snapshot): void;
}
export interface DomainModule {
  namespace: string;
  version: number;
  resource: string;
  pages?: PageRegistration[];
  commands: Record<string, DomainCommand>;
  query?(ctx: DomainReadContext, input: QueryInput): unknown;
  search?(ctx: DomainReadContext): SearchDocument[];
  metrics?(ctx: DomainReadContext): OutcomeSnapshot[];
  suggestions?(ctx: DomainReadContext): ActionRecommendation[];
  migrations?: DomainMigration[];
  isActive?(ctx: DomainReadContext): boolean;
  tick?(ctx: DomainCommandContext, elapsedMs: number): void;
}
export const contextKey = (context: Context) =>
  `${context.role}:${context.campus}:${context.viewMemberUuid}`;
export function paginate<T>(
  rows: T[],
  input: QueryInput,
  revision: number,
): PageResult<T> {
  const page = Math.max(1, Math.trunc(input.page || 1));
  const pageSize = Math.min(100, Math.max(1, Math.trunc(input.pageSize || 20)));
  return {
    items: rows.slice((page - 1) * pageSize, page * pageSize),
    total: rows.length,
    page,
    pageSize,
    revision,
  };
}
